import { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import styled from 'styled-components'

import { Button } from '@loadsmart/miranda-react'
import { toCSSValue } from '@loadsmart/miranda-react/dist/tokens'

import { analytics } from '../../core/utils/analytics'
import { type TenderCarrier, type TenderOffer } from '../domain/TenderOffer'
import { acceptTender } from '../use-cases/acceptTender'
import { rejectTender } from '../use-cases/rejectTender'
import { AcceptTenderDialog } from './AcceptTenderDialog'
import { RejectTenderDialog } from './RejectTenderDialog'
import { TenderDetails } from './TenderDetails'

const ToastShowButton = styled(Button)`
  background-color: ${toCSSValue('color-background-highlight')} !important;
  color: ${toCSSValue('color-text-link')} !important;
`

const ToastContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

export type TenderCardProps = {
  tender: TenderOffer
  disabled?: boolean
  carrier?: TenderCarrier
  highlighted?: boolean
  onTenderUpdated?: () => void
}

type ResponseStatus = 'accepted' | 'rejected' | null

function TenderAcceptedToast({
  onOpenShipment,
  canOpenShipment,
}: {
  onOpenShipment: () => void
  canOpenShipment: boolean
}) {
  return (
    <ToastContent>
      <span>Submitted with success</span>
      {canOpenShipment && (
        <ToastShowButton
          variant="tertiary"
          onClick={e => {
            e.stopPropagation()
            analytics.track('Clicked: Toast Action', {
              button_label: 'Open shipment',
              banner_label: 'Submitted with success',
            })
            onOpenShipment()
          }}
        >
          Open shipment
        </ToastShowButton>
      )}
    </ToastContent>
  )
}

export function TenderCard({
  tender,
  disabled,
  carrier,
  highlighted,
  onTenderUpdated,
}: TenderCardProps) {
  const [responseStatus, setResponseStatus] = useState<ResponseStatus>(null)
  const { push: navigate } = useHistory()
  const [isAcceptDialogOpen, setIsAcceptDialogOpen] = useState(false)
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  function handleAcceptClick() {
    setIsAcceptDialogOpen(true)
  }

  function handleRejectClick() {
    setIsRejectDialogOpen(true)
  }

  async function handleAcceptConfirm() {
    setIsAcceptDialogOpen(false)
    setIsSubmitting(true)
    try {
      const { shipment_uuid, shipment_carrier_1xp_link } = await acceptTender({
        tenderUuid: tender.id,
      })

      setResponseStatus('accepted')

      analytics.track('Accepted: Tender Offer', {
        mode: tender.mode,
        shipper: tender.shipper,
      })

      const canOpenShipment = tender.shareDetails && !!(shipment_carrier_1xp_link || shipment_uuid)

      toast.success(
        <TenderAcceptedToast
          canOpenShipment={canOpenShipment}
          onOpenShipment={() => {
            if (!canOpenShipment) return

            if (shipment_carrier_1xp_link) {
              window.location.href = shipment_carrier_1xp_link
              return
            }

            if (shipment_uuid) {
              navigate(`/all-in-one/shipment/${shipment_uuid}`)
            }
          }}
        />
      )
      onTenderUpdated?.()
    } catch {
      toast.error('Action failed. Try again')
    } finally {
      setIsSubmitting(false)
    }
  }

  function handleAcceptCancel() {
    setIsAcceptDialogOpen(false)
  }

  async function handleRejectConfirm(reason: string) {
    setIsRejectDialogOpen(false)
    setIsSubmitting(true)
    try {
      await rejectTender({ tenderUuid: tender.id, rejectReason: reason })
      setResponseStatus('rejected')
      toast.success('Submitted with success')
      analytics.track('Rejected: Tender Offer', {
        mode: tender.mode,
        shipper: tender.shipper,
        reason: reason,
      })
      onTenderUpdated?.()
    } catch {
      toast.error('Action failed. Try again')
    } finally {
      setIsSubmitting(false)
    }
  }

  function handleRejectCancel() {
    setIsRejectDialogOpen(false)
  }

  return (
    <>
      <TenderDetails
        key={tender.id}
        id={tender.id}
        tender={tender}
        carrier={carrier}
        disabled={Boolean(responseStatus || disabled)}
        onAcceptClick={handleAcceptClick}
        onRejectClick={handleRejectClick}
        highlighted={highlighted}
        responseStatus={responseStatus}
      />
      <AcceptTenderDialog
        tender={tender}
        open={isAcceptDialogOpen}
        onConfirm={handleAcceptConfirm}
        onCancel={handleAcceptCancel}
        loading={isSubmitting}
      />
      <RejectTenderDialog
        open={isRejectDialogOpen}
        onConfirm={handleRejectConfirm}
        onCancel={handleRejectCancel}
        loading={isSubmitting}
      />
    </>
  )
}
